import { displayToast, sendAppsignalError } from "../modules/utils";
import { createPopper } from "@popperjs/core";

export default class AutocompleteSearch {
  constructor(node) {
    this.element = node;

    this.searchInput = this.element.querySelector(".js-autocomplete-search-input");
    this.searchInputWrapper = this.element.querySelector(".js-autocomplete-search-input-wrapper");
    this.formWrapper = this.element.querySelector(".js-autocomplete-search-form-wrapper");
    this.countryInput = this.element.querySelector(".js-autocomplete-search-country-input");
    this.autocompleteToggleWrapper = this.element.querySelector(".js-autocomplete-search-toggle-wrapper");
    this.autocompleteToggle = this.element.querySelector(".js-autocomplete-search-toggle");
    this.inputs = this.element.querySelectorAll(".js-autocomplete-search-inputs");
    this.addressesWrapper = this.element.querySelector(".js-autocomplete-search-addresses-wrapper");
    this.addressOptionTemplate = this.element.querySelector(".js-autocomplete-search-address-option-template");
    this.noResultsTemplate = this.element.querySelector(".js-autocomplete-search-no-results-template");

    this.dataRegionFieldKey = this.element.dataset.regionField;
    this.searchUrl = this.element.dataset.searchUrl;
    this.contextUrl = this.element.dataset.contextUrl;
    this.timeoutInterval = null;
    this.boundFunction = this.checkIfWithinBounds.bind(this);
    this.addedOutOfBoundsEventListener = false;

    this.initialize();
  }

  initialize() {
    window.addEventListener("beyuna-classes-initialized", () => {
      this.regionSelectInstance = window.Beyuna.objects["region-select"].autocomplete;
    });

    this.countryInput.addEventListener("change", (e) => {
      this.enabled = e.target.dataset.enableAutoCompleteSearch === "true";
      this.searchInput.value = ""; // Reset the search input when the country changes

      this.toggleFormAvailability();
    });

    this.autocompleteToggle.addEventListener("click", () => {
      this.enabled = false;

      document.removeEventListener("click", this.boundFunction);
      this.addedOutOfBoundsEventListener = false;

      this.toggleFormAvailability();
    });

    this.searchInput.addEventListener("input", () => {
      if (this.timeoutInterval) {
        clearTimeout(this.timeoutInterval);
      }

      // We throttle the input to prevent too many requests
      this.timeoutInterval = setTimeout(() => {
        this.fetchSuggestions();
      }, 300);

      // Fix for wrong position when used within a modal
      if (!this.dropdownPositionSet) {
        this.setDropdownPosition();
      }
    });
  }

  toggleFormAvailability() {
    this.searchInputWrapper.classList.toggle("d-none", !this.enabled);
    this.autocompleteToggleWrapper.classList.toggle("d-none", !this.enabled);

    this.inputs.forEach((input) => {
      input.readOnly = this.enabled;
      input.classList.toggle("disabled", this.enabled);
    });
  }

  setDropdownPosition() {
    createPopper(this.searchInput, this.addressesWrapper, {
      placement: "bottom-start",
      modifiers: [
        {
          name: "sameWidth",
          enabled: true,
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          phase: "beforeWrite",
          requires: ["computeStyles"],
        },
        {
          name: "flip",
          options: {
            padding: {
              top: 16,
            },
          },
        },
        {
          name: "offset",
          options: {
            offset: [0, 16],
          },
        },
      ],
    });

    this.dropdownPositionSet = true;
  }

  fetchSuggestions(context) {
    const formData = new FormData();
    let url = this.contextUrl;
    let contextValue = context;

    if (context) {
      this.addressesWrapper.classList.add("is-loading");

      formData.append("sessionId", this.sessionId);
    } else {
      this.searchInputWrapper.classList.add("loading");

      url = this.searchUrl;
      contextValue = this.countryInput.dataset.valueCode;
    }

    formData.append(this.searchInput.name, this.searchInput.value);
    formData.append("context", contextValue);

    fetch(url, {
      retries: 3,
      retryDelay: 1000,
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: formData,
    })
      .then((response) => {
        this.response = response;

        if (response.status === 200) {
          return response.json();
        }

        throw new Error("Something went wrong");
      })
      .then((data) => {
        context ? this.handleContextSearch(data) : this.handleInitialSearch(data);
      })
      .catch((e) => {
        console.error(e);
        displayToast(this.element.dataset.errorMessage);
        sendAppsignalError(e, "AutocompleteSearch", {
          response: this.response,
        });
      })
      .finally(() => {
        context ? this.addressesWrapper.classList.remove("is-loading") : this.searchInputWrapper.classList.remove("loading");
      });
  }

  handleContextSearch(data) {
    if (!this.addedOutOfBoundsEventListener) {
      document.addEventListener("click", this.boundFunction);
      this.addedOutOfBoundsEventListener = true;
    }

    if (data.length === 0) {
      this.addressesWrapper.innerHTML = "";

      const noResultsElement = this.noResultsTemplate.cloneNode(true);
      this.addressesWrapper.appendChild(noResultsElement.content);
      return;
    }

    if (data.length === 1 && data[0].address) {
      this.populateForm(data[0].address);
      return;
    }

    this.showAddressOptions(data);
  }

  handleInitialSearch(data) {
    this.sessionId = data.sessionId;
    this.showAddressOptions(data.suggestions);

    if (!this.addedOutOfBoundsEventListener) {
      document.addEventListener("click", this.boundFunction);
      this.addedOutOfBoundsEventListener = true;
    }
  }

  showAddressOptions(data) {
    this.addressesWrapper.innerHTML = "";

    if (data.length === 0) {
      const noResultsElement = this.noResultsTemplate.cloneNode(true);
      this.addressesWrapper.appendChild(noResultsElement.content);
      return;
    }

    data.map((address) => {
      const addressOption = this.addressOptionTemplate.cloneNode(true).content;
      const radio = addressOption.querySelector("input[type='radio']");

      this.populateAddressOption(addressOption, address);

      radio.addEventListener("change", () => {
        this.searchInput.value = address.value;

        if (address.context != null) {
          this.fetchSuggestions(address.context);
        } else {
          this.populateForm(address.address);
        }

        document.removeEventListener("click", this.boundFunction);
        this.addedOutOfBoundsEventListener = false;
      });

      this.addressesWrapper.appendChild(addressOption);
    });
  }

  populateAddressOption(scope, address) {
    const element = scope.querySelector(".js-autocomplete-search-value");
    const countryElement = scope.querySelector(".js-autocomplete-search-country");
    const radioInput = scope.querySelector(".js-autocomplete-search-address-address-option-value");

    if (element) {
      // Makes the search term bold
      const key = this.searchInput.value;
      const regex = new RegExp(key, "i");
      const match = address.value.match(regex);

      element.innerHTML = address.value.replace(regex, `<strong>${match}</strong>`);
    } else {
      displayToast(this.element.dataset.errorMessage);
      sendAppsignalError(key, "AutocompleteSearch", {});
    }

    if (countryElement) {
      countryElement.innerHTML = this.element.querySelector(".js-autocomplete-search-selected-country").innerHTML;
    } else {
      displayToast(this.element.dataset.errorMessage);
      sendAppsignalError(key, "AutocompleteSearch", {});
    }

    if (radioInput) {
      radioInput.value = address.context;
    } else {
      displayToast(this.element.dataset.errorMessage);
      sendAppsignalError(key, "AutocompleteSearch", {});
    }
  }

  populateForm(address) {
    this.addressesWrapper.innerHTML = "";

    this.formWrapper.querySelectorAll("input").forEach((input) => {
      input.value = "";
    });

    const addressKeys = Object.keys(address);

    addressKeys.forEach((key) => {
      const input = this.element.querySelector(`[data-field="${key}"]`);

      if (!input) return;

      if (key === this.dataRegionFieldKey) {
        this.regionSelectInstance.setRegion(address[key]);
      } else {
        input.value = address[key];
      }
    });
  }

  checkIfWithinBounds(e) {
    const target = e.target;

    if (this.addressesWrapper == target) return;

    if (!this.addressesWrapper.contains(target)) {
      this.addressesWrapper.innerHTML = "";

      document.removeEventListener("click", this.boundFunction);
      this.addedOutOfBoundsEventListener = false;
    }
  }
}
